import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Send, 
  Sparkles, 
  ScrollText, 
  Scale,
  User,
  ArrowRight,
  BookOpen,
  PlusCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { motion } from "framer-motion";
import { cn } from "../lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../lib/api";
import ChatHistorySidebar from "../components/ChatHistorySidebar";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { visit } from "unist-util-visit";
import {
  InlineCitation,
  InlineCitationText,
  InlineCitationCard,
  InlineCitationCardTrigger,
  InlineCitationCardBody,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselItem,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationCarouselPrev,
  InlineCitationCarouselNext,
  InlineCitationSource,
} from "../components/ai-elements/inline-citation";

type Message = {
  id: number;
  role: "user" | "ai";
  content: string;
  sources?: Array<{
    title: string;
    type: "olt" | "bylaw";
    date: string;
    link: string;
    case_number?: string; // For OLT cases
    bylaw_number?: string; // For bylaws
  }>;
};

// Helper function to extract mentioned sources from content
function getMentionedSources(content: string, allSources: Message["sources"] = []): Message["sources"] {
  if (!allSources || allSources.length === 0) return [];
  
  const mentioned: Message["sources"] = [];
  
  console.log("🔍 getMentionedSources - Checking", allSources.length, "sources against content");
  console.log("Content snippet:", content.substring(0, 500));
  
  // Patterns to match in the content
  for (const source of allSources) {
    let isMentioned = false;
    
    // Infer type from case_number or bylaw_number if type is not set
    const inferredType = source.type || (source.case_number ? "olt" : source.bylaw_number ? "bylaw" : undefined);
    
    console.log("Checking source:", {
      type: source.type,
      inferredType: inferredType,
      case_number: source.case_number,
      bylaw_number: source.bylaw_number,
      title: source.title?.substring(0, 50)
    });
    
    // For OLT cases: match case numbers like "OLT-23-001234"
    if ((inferredType === "olt" || source.case_number) && source.case_number) {
      const escaped = source.case_number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const casePattern = new RegExp(escaped, 'gi');
      const matches = content.match(casePattern);
      console.log(`  OLT pattern "${escaped}" matches:`, matches ? matches.length : 0, matches);
      if (matches && matches.length > 0) {
        isMentioned = true;
      }
    }
    
    // For bylaws: match bylaw numbers like "By-law 793-2025" or "Bylaw 143-2025"
    if ((inferredType === "bylaw" || source.bylaw_number) && source.bylaw_number) {
      const escaped = source.bylaw_number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const bylawPatterns = [
        new RegExp(`(?:By-law|Bylaw)\\s+${escaped}`, 'gi'),
        new RegExp(escaped, 'gi')
      ];
      const matches = bylawPatterns.some(pattern => {
        const result = content.match(pattern);
        console.log(`  Bylaw pattern "${pattern.source}" matches:`, result ? result.length : 0, result);
        return result && result.length > 0;
      });
      if (matches) {
        isMentioned = true;
      }
    }
    
    // Also check if title is mentioned (for cases where number might not be in content)
    if (source.title && content.toLowerCase().includes(source.title.toLowerCase().substring(0, 20))) {
      console.log(`  Title match found for: ${source.title.substring(0, 20)}`);
      isMentioned = true;
    }
    
    if (isMentioned) {
      console.log("✅ Source is mentioned:", source.case_number || source.bylaw_number);
      mentioned.push(source);
    } else {
      console.log("❌ Source not mentioned:", source.case_number || source.bylaw_number);
    }
  }
  
  console.log("📊 Total mentioned sources:", mentioned.length);
  return mentioned;
}

// Component to render markdown with inline citations
function MarkdownWithCitations({ content, sources }: { content: string; sources: Message["sources"] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const mentionedSources = useMemo(() => {
    const mentioned = getMentionedSources(content, sources);
    console.log("MarkdownWithCitations - Content preview:", content.substring(0, 300));
    console.log("MarkdownWithCitations - All sources:", sources);
    console.log("MarkdownWithCitations - Mentioned sources:", mentioned);
    return mentioned;
  }, [content, sources]);
  
  // Process content to replace case numbers with HTML spans that we can render
  const processedContent = useMemo(() => {
    if (!content || mentionedSources.length === 0) return content;
    
    let processed = content;
    const replacements: Array<{ index: number; length: number; source: Message["sources"][0]; text: string }> = [];
    
    // Find all matches
    for (const source of mentionedSources) {
      let patterns: RegExp[] = [];
      
      // Infer type from case_number or bylaw_number if type is not set
      const inferredType = source.type || (source.case_number ? "olt" : source.bylaw_number ? "bylaw" : undefined);
      
      if ((inferredType === "olt" || source.case_number) && source.case_number) {
        // Match OLT case numbers - be more flexible with word boundaries
        const escaped = source.case_number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        patterns = [
          new RegExp(`\\b${escaped}\\b`, 'gi'),
          new RegExp(escaped, 'gi') // Fallback without word boundaries
        ];
        console.log(`🔍 Processing OLT case ${source.case_number} with patterns:`, patterns.map(p => p.source));
      } else if ((inferredType === "bylaw" || source.bylaw_number) && source.bylaw_number) {
        const escaped = source.bylaw_number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        patterns = [
          new RegExp(`\\b(?:By-law|Bylaw)\\s+${escaped}\\b`, 'gi'),
          new RegExp(escaped, 'gi')
        ];
        console.log(`🔍 Processing bylaw ${source.bylaw_number} with patterns:`, patterns.map(p => p.source));
      }
      
      for (const pattern of patterns) {
        let match;
        pattern.lastIndex = 0;
        while ((match = pattern.exec(processed)) !== null) {
          // Check if this match is already in a citation span
          const beforeMatch = processed.substring(Math.max(0, match.index - 50), match.index);
          const afterMatch = processed.substring(match.index + match[0].length, Math.min(processed.length, match.index + match[0].length + 50));
          if (!beforeMatch.includes('data-citation') && !afterMatch.includes('data-citation')) {
            replacements.push({
              index: match.index,
              length: match[0].length,
              source: source,
              text: match[0]
            });
          }
        }
      }
    }
    
    // Sort by index descending to replace from end to start
    replacements.sort((a, b) => b.index - a.index);
    
    // Remove duplicates and overlapping
    const filtered: typeof replacements = [];
    for (const r of replacements) {
      const overlaps = filtered.some(f => 
        (r.index >= f.index && r.index < f.index + f.length) ||
        (f.index >= r.index && f.index < r.index + r.length)
      );
      if (!overlaps) {
        filtered.push(r);
      }
    }
    
    console.log(`📝 Found ${replacements.length} potential replacements, ${filtered.length} after filtering`);
    
    // Replace from end to start
    for (const replacement of filtered) {
      const before = processed.substring(0, replacement.index);
      const after = processed.substring(replacement.index + replacement.length);
      const citationId = `cite-${replacement.source.link?.replace(/[^a-zA-Z0-9]/g, '-') || 'unknown'}-${replacement.index}`;
      const htmlSpan = `<span data-citation-id="${citationId}" data-citation-link="${(replacement.source.link || '#').replace(/"/g, '&quot;')}" data-citation-text="${replacement.text.replace(/"/g, '&quot;')}" class="inline-citation-marker">${replacement.text}</span>`;
      processed = before + htmlSpan + after;
      console.log("✅ Replaced text with citation span:", {
        original: replacement.text,
        index: replacement.index,
        source: replacement.source.case_number || replacement.source.bylaw_number,
        htmlSpan: htmlSpan.substring(0, 150)
      });
    }
    
    if (filtered.length > 0) {
      console.log("📄 Processed content preview (with citations):", processed.substring(0, 500));
    } else {
      console.log("⚠️ No replacements made. Processed content unchanged.");
    }
    return processed;
  }, [content, mentionedSources]);
  
  // Create a remark plugin to replace text nodes with citation components
  const citationRemarkPlugin = useMemo(() => {
    if (!mentionedSources || mentionedSources.length === 0) return undefined;
    
    return () => {
      return (tree: any) => {
        visit(tree, 'text', (node: any, index: number, parent: any) => {
          if (!node.value || !parent) return;
          
          for (const source of mentionedSources) {
            let patterns: RegExp[] = [];
            
            if (source.type === "olt" && source.case_number) {
              const escaped = source.case_number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              patterns = [
                new RegExp(`\\b${escaped}\\b`, 'gi'),
                new RegExp(escaped, 'gi')
              ];
            } else if (source.type === "bylaw" && source.bylaw_number) {
              const escaped = source.bylaw_number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              patterns = [
                new RegExp(`\\b(?:By-law|Bylaw)\\s+${escaped}\\b`, 'gi'),
                new RegExp(escaped, 'gi')
              ];
            }
            
            for (const pattern of patterns) {
              const matches = [...node.value.matchAll(pattern)];
              if (matches.length > 0) {
                // Replace the text node with a mix of text and JSX elements
                const parts: any[] = [];
                let lastIndex = 0;
                
                for (const match of matches) {
                  if (match.index === undefined) continue;
                  
                  // Add text before match
                  if (match.index > lastIndex) {
                    parts.push({
                      type: 'text',
                      value: node.value.substring(lastIndex, match.index)
                    });
                  }
                  
                  // Add citation element
                  parts.push({
                    type: 'mdxJsxTextElement',
                    name: 'CitationSpan',
                    attributes: [
                      {
                        type: 'mdxJsxAttribute',
                        name: 'citationId',
                        value: `cite-${source.link?.replace(/[^a-zA-Z0-9]/g, '-') || 'unknown'}-${match.index}`
                      },
                      {
                        type: 'mdxJsxAttribute',
                        name: 'link',
                        value: source.link || '#'
                      },
                      {
                        type: 'mdxJsxAttribute',
                        name: 'citedText',
                        value: match[0]
                      },
                      {
                        type: 'mdxJsxAttribute',
                        name: 'source',
                        value: JSON.stringify(source)
                      }
                    ],
                    children: [{ type: 'text', value: match[0] }]
                  });
                  
                  lastIndex = match.index + match[0].length;
                }
                
                // Add remaining text
                if (lastIndex < node.value.length) {
                  parts.push({
                    type: 'text',
                    value: node.value.substring(lastIndex)
                  });
                }
                
                // Replace the single text node with multiple nodes
                if (parts.length > 1) {
                  parent.children.splice(index, 1, ...parts);
                  return; // Exit early since we've replaced the node
                }
              }
            }
          }
        });
      };
    };
  }, [mentionedSources]);
  
  // Post-render effect to replace case numbers in DOM with citation components
  useEffect(() => {
    if (!containerRef.current || mentionedSources.length === 0) return;
    
    const timeout = setTimeout(() => {
      // Find all paragraph elements that might contain case numbers
      const paragraphs = containerRef.current?.querySelectorAll('p');
      if (!paragraphs) return;
      
      console.log(`🔍 Checking ${paragraphs.length} paragraphs for case numbers`);
      
      paragraphs.forEach((p) => {
        const text = p.textContent || '';
        let hasMatch = false;
        let replacementParts: Array<{ type: 'text' | 'citation'; content: string; source?: Message["sources"][0] }> = [];
        let lastIndex = 0;
        
        // Find all case numbers in this paragraph
        for (const source of mentionedSources) {
          if (source.case_number && text.includes(source.case_number)) {
            const escaped = source.case_number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const pattern = new RegExp(escaped, 'gi');
            let match;
            const matches: Array<{ index: number; length: number; source: Message["sources"][0]; text: string }> = [];
            
            while ((match = pattern.exec(text)) !== null) {
              matches.push({
                index: match.index,
                length: match[0].length,
                source: source,
                text: match[0]
              });
            }
            
            // Sort by index
            matches.sort((a, b) => a.index - b.index);
            
            // Remove overlaps
            const filtered: typeof matches = [];
            for (const m of matches) {
              const overlaps = filtered.some(f => 
                (m.index >= f.index && m.index < f.index + f.length) ||
                (f.index >= m.index && f.index < m.index + m.length)
              );
              if (!overlaps) {
                filtered.push(m);
              }
            }
            
            // Build replacement parts
            for (const m of filtered) {
              if (m.index > lastIndex) {
                replacementParts.push({
                  type: 'text',
                  content: text.substring(lastIndex, m.index)
                });
              }
              replacementParts.push({
                type: 'citation',
                content: m.text,
                source: m.source
              });
              lastIndex = m.index + m.length;
              hasMatch = true;
            }
          }
        }
        
        // If we found matches, replace the paragraph content
        if (hasMatch && replacementParts.length > 0) {
          // Add remaining text
          if (lastIndex < text.length) {
            replacementParts.push({
              type: 'text',
              content: text.substring(lastIndex)
            });
          }
          
          // Clear the paragraph
          p.innerHTML = '';
          
          // Render new content with React
          import('react-dom/client').then(({ createRoot }) => {
            const root = createRoot(p);
            
            const elements = replacementParts.map((part, idx) => {
              if (part.type === 'text') {
                return React.createElement('span', { key: idx }, part.content);
              } else if (part.type === 'citation' && part.source) {
                const allSourcesForCitation = mentionedSources.filter(s => 
                  (s.case_number === part.source?.case_number) ||
                  (s.bylaw_number === part.source?.bylaw_number)
                );
                const sourcesToShow = allSourcesForCitation.length > 0 ? allSourcesForCitation : [part.source];
                
                return React.createElement(
                  InlineCitation,
                  { key: idx },
                  React.createElement(InlineCitationText, null, part.content),
                  React.createElement(
                    InlineCitationCard,
                    null,
                    React.createElement(
                      InlineCitationCardTrigger,
                      { sources: sourcesToShow.map(s => s.link || '#') },
                      React.createElement(
                        InlineCitationCardBody,
                        null,
                        React.createElement(
                          InlineCitationCarousel,
                          null,
                          React.createElement(
                            InlineCitationCarouselHeader,
                            null,
                            sourcesToShow.length > 1 && [
                              React.createElement(InlineCitationCarouselPrev, { key: 'prev' }),
                              React.createElement(InlineCitationCarouselNext, { key: 'next' })
                            ],
                            React.createElement(InlineCitationCarouselIndex, { key: 'index' })
                          ),
                          React.createElement(
                            InlineCitationCarouselContent,
                            null,
                            sourcesToShow.map((src, srcIdx) =>
                              React.createElement(
                                InlineCitationCarouselItem,
                                { key: srcIdx },
                                React.createElement(InlineCitationSource, {
                                  title: src.title,
                                  url: src.link || '#',
                                  description: src.date ? `Date: ${src.date}` : undefined
                                })
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                );
              }
              return null;
            });
            
            root.render(React.createElement(React.Fragment, null, ...elements));
            console.log(`✅ Replaced paragraph with ${replacementParts.length} parts`);
          });
        }
      });
    }, 300);
    
    return () => clearTimeout(timeout);
  }, [processedContent, mentionedSources]);
  
  // Create a rehype plugin to ensure citation spans are properly handled
  const citationRehypePlugin = () => {
    return (tree: any) => {
      visit(tree, 'element', (node: any) => {
        if (node.tagName === 'span' && node.properties?.['data-citation-id']) {
          // Ensure the properties are accessible
          console.log("Rehype plugin found citation span:", {
            tagName: node.tagName,
            properties: node.properties,
            children: node.children
          });
          // Keep the element as-is but ensure properties are preserved
          node.properties = node.properties || {};
        }
      });
    };
  };

  return (
    <div ref={containerRef}>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw, citationRehypePlugin]}
        components={{
        // Handle our custom CitationSpan JSX element
        CitationSpan: ({ citationId, link, citedText, source, ...props }: any) => {
          const sourceObj = source ? JSON.parse(source) : mentionedSources.find(s => s.link === link);
          if (sourceObj) {
            const allSourcesForCitation = mentionedSources.filter(s => 
              (s.type === "olt" && s.case_number === sourceObj.case_number) ||
              (s.type === "bylaw" && s.bylaw_number === sourceObj.bylaw_number)
            );
            const sourcesToShow = allSourcesForCitation.length > 0 ? allSourcesForCitation : [sourceObj];
            
            return (
              <InlineCitation>
                <InlineCitationText>{citedText}</InlineCitationText>
                <InlineCitationCard>
                  <InlineCitationCardTrigger sources={sourcesToShow.map(s => s.link || '#')}>
                    <InlineCitationCardBody>
                      <InlineCitationCarousel>
                        <InlineCitationCarouselHeader>
                          {sourcesToShow.length > 1 && (
                            <>
                              <InlineCitationCarouselPrev />
                              <InlineCitationCarouselNext />
                            </>
                          )}
                          <InlineCitationCarouselIndex />
                        </InlineCitationCarouselHeader>
                        <InlineCitationCarouselContent>
                          {sourcesToShow.map((src, idx) => (
                            <InlineCitationCarouselItem key={idx}>
                              <InlineCitationSource
                                title={src.title}
                                url={src.link || '#'}
                                description={src.date ? `Date: ${src.date}` : undefined}
                              />
                            </InlineCitationCarouselItem>
                          ))}
                        </InlineCitationCarouselContent>
                      </InlineCitationCarousel>
                    </InlineCitationCardBody>
                  </InlineCitationCardTrigger>
                </InlineCitationCard>
              </InlineCitation>
            );
          }
          return <span>{citedText}</span>;
        },
        // Custom span renderer for citations
        span: ({ node, ...props }: any) => {
          // Check both props and node.properties for data attributes (rehypeRaw puts them in node.properties)
          const citationId = props['data-citation-id'] || node?.properties?.['data-citation-id'];
          const link = props['data-citation-link'] || node?.properties?.['data-citation-link'];
          const citedText = props['data-citation-text'] || node?.properties?.['data-citation-text'] || props.children;
          
          console.log("Span renderer called:", { 
            citationId, 
            link, 
            citedText, 
            propsKeys: Object.keys(props),
            nodeProperties: node?.properties,
            nodeType: node?.type,
            children: props.children,
            nodeTagName: node?.tagName
          });
          
          if (citationId && link) {
            const source = mentionedSources.find(s => s.link === link || s.link === decodeURIComponent(link));
            console.log("Found source for citation:", source);
            if (source) {
              // Get all sources for this citation (in case multiple sources reference the same case)
              const allSourcesForCitation = mentionedSources.filter(s => 
                (s.type === "olt" && s.case_number === source.case_number) ||
                (s.type === "bylaw" && s.bylaw_number === source.bylaw_number)
              );
              
              const sourcesToShow = allSourcesForCitation.length > 0 ? allSourcesForCitation : [source];
              
              console.log("Rendering citation with sources:", sourcesToShow);
              
              return (
                <InlineCitation {...props}>
                  <InlineCitationText>{citedText}</InlineCitationText>
                  <InlineCitationCard>
                    <InlineCitationCardTrigger sources={sourcesToShow.map(s => s.link || '#')}>
                      <InlineCitationCardBody>
                        <InlineCitationCarousel>
                          <InlineCitationCarouselHeader>
                            {sourcesToShow.length > 1 && (
                              <>
                                <InlineCitationCarouselPrev />
                                <InlineCitationCarouselNext />
                              </>
                            )}
                            <InlineCitationCarouselIndex />
                          </InlineCitationCarouselHeader>
                          <InlineCitationCarouselContent>
                            {sourcesToShow.map((src, idx) => (
                              <InlineCitationCarouselItem key={idx}>
                                <InlineCitationSource
                                  title={src.title}
                                  url={src.link || '#'}
                                  description={src.date ? `Date: ${src.date}` : undefined}
                                />
                              </InlineCitationCarouselItem>
                            ))}
                          </InlineCitationCarouselContent>
                        </InlineCitationCarousel>
                      </InlineCitationCardBody>
                    </InlineCitationCardTrigger>
                  </InlineCitationCard>
                </InlineCitation>
              );
            }
          }
          return <span {...props} />;
        },
        // Handle raw HTML nodes that might contain our citation spans
        html: ({ node, ...props }: any) => {
          console.log("HTML node renderer called:", { node, props });
          // Try to parse the HTML and extract citation data
          if (typeof node.children === 'string') {
            const htmlContent = node.children;
            const citationMatch = htmlContent.match(/data-citation-id="([^"]+)"[^>]*data-citation-link="([^"]+)"[^>]*data-citation-text="([^"]+)"/);
            if (citationMatch) {
              const [, citationId, link, citedText] = citationMatch;
              const source = mentionedSources.find(s => s.link === link || s.link === decodeURIComponent(link));
              if (source) {
                const allSourcesForCitation = mentionedSources.filter(s => 
                  (s.type === "olt" && s.case_number === source.case_number) ||
                  (s.type === "bylaw" && s.bylaw_number === source.bylaw_number)
                );
                const sourcesToShow = allSourcesForCitation.length > 0 ? allSourcesForCitation : [source];
                
                return (
                  <InlineCitation>
                    <InlineCitationText>{citedText}</InlineCitationText>
                    <InlineCitationCard>
                      <InlineCitationCardTrigger sources={sourcesToShow.map(s => s.link || '#')}>
                        <InlineCitationCardBody>
                          <InlineCitationCarousel>
                            <InlineCitationCarouselHeader>
                              {sourcesToShow.length > 1 && (
                                <>
                                  <InlineCitationCarouselPrev />
                                  <InlineCitationCarouselNext />
                                </>
                              )}
                              <InlineCitationCarouselIndex />
                            </InlineCitationCarouselHeader>
                            <InlineCitationCarouselContent>
                              {sourcesToShow.map((src, idx) => (
                                <InlineCitationCarouselItem key={idx}>
                                  <InlineCitationSource
                                    title={src.title}
                                    url={src.link || '#'}
                                    description={src.date ? `Date: ${src.date}` : undefined}
                                  />
                                </InlineCitationCarouselItem>
                              ))}
                            </InlineCitationCarouselContent>
                          </InlineCitationCarousel>
                        </InlineCitationCardBody>
                      </InlineCitationCardTrigger>
                    </InlineCitationCard>
                  </InlineCitation>
                );
              }
            }
          }
          // Fallback: render as raw HTML (not ideal but works)
          return <span dangerouslySetInnerHTML={{ __html: node.children }} />;
        },
        // Clean, minimal styling matching Elasticsearch dashboard
        p: ({node, ...props}: any) => (
          <p className="mb-4 last:mb-0 leading-7 text-neutral-800 dark:text-neutral-200" {...props} />
        ),
        ul: ({node, ...props}) => (
          <ul className="ml-5 mb-4 list-disc space-y-1.5 text-neutral-800 dark:text-neutral-200" {...props} />
        ),
        ol: ({node, ...props}) => (
          <ol className="ml-5 mb-4 list-decimal space-y-1.5 text-neutral-800 dark:text-neutral-200" {...props} />
        ),
        li: ({node, ...props}) => (
          <li className="leading-6 pl-1" {...props} />
        ),
        h1: ({node, children, ...props}) => (
          <h1 className="text-xl font-semibold mb-3 mt-5 first:mt-0 text-neutral-900 dark:text-neutral-100" {...props}>
            {children}
          </h1>
        ),
        h2: ({node, children, ...props}) => (
          <h2 className="text-lg font-semibold mb-2.5 mt-5 first:mt-0 text-neutral-900 dark:text-neutral-100" {...props}>
            {children}
          </h2>
        ),
        h3: ({node, children, ...props}) => (
          <h3 className="text-base font-semibold mb-2 mt-4 first:mt-0 text-neutral-900 dark:text-neutral-100" {...props}>
            {children}
          </h3>
        ),
        h4: ({node, children, ...props}) => (
          <h4 className="text-sm font-semibold mb-2 mt-3 first:mt-0 text-neutral-900 dark:text-neutral-100" {...props}>
            {children}
          </h4>
        ),
        strong: ({node, ...props}) => (
          <strong className="font-semibold text-neutral-900 dark:text-neutral-100" {...props} />
        ),
        em: ({node, ...props}) => (
          <em className="italic text-neutral-800 dark:text-neutral-200" {...props} />
        ),
        code: ({node, inline, ...props}: any) => 
          inline ? (
            <code className="bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-xs font-mono text-neutral-900 dark:text-neutral-100" {...props} />
          ) : (
            <code className="block bg-neutral-100 dark:bg-neutral-800 p-3 rounded-md text-xs font-mono overflow-x-auto mb-4 text-neutral-900 dark:text-neutral-100" {...props} />
          ),
        blockquote: ({node, ...props}) => (
          <blockquote className="border-l-4 border-primary/50 pl-4 my-4 italic text-neutral-600 dark:text-neutral-400" {...props} />
        ),
        a: ({node, children, ...props}: any) => (
          <a className="text-primary hover:underline font-medium" target="_blank" rel="noopener noreferrer" {...props}>
            {children || props.href}
          </a>
        ),
        hr: ({node, ...props}) => (
          <hr className="my-6 border-neutral-200 dark:border-neutral-700" {...props} />
        ),
        table: ({node, ...props}) => (
          <div className="overflow-x-auto my-4">
            <table className="min-w-full border border-neutral-200 dark:border-neutral-700 rounded-md" {...props} />
          </div>
        ),
        thead: ({node, ...props}) => (
          <thead className="bg-neutral-50 dark:bg-neutral-800" {...props} />
        ),
        th: ({node, ...props}) => (
          <th className="border border-neutral-200 dark:border-neutral-700 px-4 py-2 text-left font-semibold text-neutral-900 dark:text-neutral-100" {...props} />
        ),
        td: ({node, ...props}) => (
          <td className="border border-neutral-200 dark:border-neutral-700 px-4 py-2 text-neutral-700 dark:text-neutral-300" {...props} />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  );
}

export default function Records() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"olt" | "bylaws">("olt");
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check if company has any municipalities monitoring
  const { data: monitoredMunicipalities } = useQuery({
    queryKey: ['company-municipalities-all'],
    queryFn: async () => {
      try {
        const data = await apiClient.companyMunicipalities.listAll();
        return data;
      } catch (error) {
        console.error('[Records] Error fetching municipalities:', error);
        return [];
      }
    },
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Function to load a specific conversation by ID
  const loadConversationById = async (conversationId: number) => {
    try {
      const data = await apiClient.recordsConversations.get(conversationId);
      const loadedMessages: Message[] = data.messages.map((msg: any) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        sources: msg.sources || []
      }));
      setMessages(loadedMessages);
    } catch (error) {
      console.error("Error loading conversation:", error);
      setMessages([]);
    }
  };

  // OLT search mutation
  const oltSearchMutation = useMutation({
    mutationFn: (searchQuery: string) => apiClient.olt.search(searchQuery, currentConversationId || undefined),
    onSuccess: (data) => {
      console.log("OLT search response:", data);
      // Ensure we have valid data and it's not the mock response
      if (!data || !data.response) {
        console.error("Invalid OLT search response:", data);
        const errorMsg: Message = {
          id: Date.now() + 1,
          role: "ai",
          content: "I apologize, but I encountered an error while searching. Please try again.",
          sources: []
        };
        setIsTyping(false);
        setMessages(prev => [...prev, errorMsg]);
        return;
      }
      
      // Update conversation ID if this is a new conversation
      if (data.conversation_id && !currentConversationId) {
        setCurrentConversationId(data.conversation_id);
        // Refresh the sidebar to show the new conversation
        queryClient.invalidateQueries({
          queryKey: ["records-conversations"]
        });
      }
      
      const aiMsg: Message = {
        id: Date.now() + 1,
        role: "ai",
        content: data.response || "I found some relevant OLT decisions for your query.",
        sources: data.sources?.map((source: any) => {
          // Extract case number from various possible fields
          const caseNum = source.case_number || 
                         source.title?.match(/OLT-\d{2}-\d{6}/)?.[0] ||
                         source.link?.match(/OLT-\d{2}-\d{6}/)?.[0];
          console.log("OLT Source mapping:", { 
            source, 
            caseNum, 
            title: source.title,
            link: source.link,
            url: source.url,
            hasCaseNumber: !!source.case_number
          });
          return {
            title: source.title || caseNum || "Unknown",
            type: "olt",
            date: source.date || "",
            link: source.link || source.url || (caseNum ? `https://www.omb.gov.on.ca/e-decisions/${caseNum}.PDF` : "#"),
            case_number: caseNum || undefined,
            bylaw_number: undefined
          };
        }) || []
      };
      console.log("OLT Message sources:", aiMsg.sources);
      console.log("OLT Message content preview:", aiMsg.content.substring(0, 300));
      setIsTyping(false);
      setMessages(prev => [...prev, aiMsg]);
    },
    onError: (error: any) => {
      console.error("OLT search error:", error);
      
      // Check if it's a "conversation too long" or validation error
      const errorDetail = error?.response?.data?.detail || error?.message || "";
      const errorLower = errorDetail.toLowerCase();
      const isConversationTooLong = errorLower.includes("conversation has become too long") || 
                                    errorLower.includes("too long for requested model");
      const isValidationError = errorLower.includes("validation error") || 
                                errorLower.includes("encountered a validation error") ||
                                errorLower.includes("content must be present");
      
      let errorContent: string;
      if ((isConversationTooLong || isValidationError) && currentConversationId) {
        if (isConversationTooLong) {
          errorContent = "⚠️ **Conversation Too Long**\n\nThe conversation history has exceeded the model's context limit. Please start a new conversation by clicking 'New Conversation' in the sidebar to continue.";
        } else {
          errorContent = "⚠️ **Conversation Error**\n\nThe conversation has encountered a validation error. Please start a new conversation by clicking 'New Conversation' in the sidebar to continue.";
        }
        // Clear the conversation ID so the next query will start fresh
        setCurrentConversationId(null);
        // Refresh the sidebar to update the conversation list
        queryClient.invalidateQueries({
          queryKey: ["records-conversations"]
        });
      } else {
        errorContent = "I apologize, but I encountered an error while searching. Please try again.";
      }
      
      const errorMsg: Message = {
        id: Date.now() + 1,
        role: "ai",
        content: errorContent,
        sources: []
      };
      setIsTyping(false);
      setMessages(prev => [...prev, errorMsg]);
    }
  });

  // Bylaws search mutation
  const bylawsSearchMutation = useMutation({
    mutationFn: (searchQuery: string) => apiClient.bylaws.search(searchQuery, currentConversationId || undefined),
    onSuccess: (data) => {
      console.log("Bylaws search response:", data);
      // Ensure we have valid data
      if (!data || !data.response) {
        console.error("Invalid Bylaws search response:", data);
        const errorMsg: Message = {
          id: Date.now() + 1,
          role: "ai",
          content: "I apologize, but I encountered an error while searching. Please try again.",
          sources: []
        };
        setIsTyping(false);
        setMessages(prev => [...prev, errorMsg]);
        return;
      }
      
      // Update conversation ID if this is a new conversation
      if (data.conversation_id && !currentConversationId) {
        setCurrentConversationId(data.conversation_id);
        // Refresh the sidebar to show the new conversation
        queryClient.invalidateQueries({
          queryKey: ["records-conversations"]
        });
      }
      
      const aiMsg: Message = {
        id: Date.now() + 1,
        role: "ai",
        content: data.response || "I found some relevant bylaws for your query.",
        sources: data.sources?.map((source: any) => {
          const bylawNum = source.bylaw_number || source.title?.match(/(\d+-\d{4})/)?.[0];
          console.log("Bylaw Source mapping:", { source, bylawNum, title: source.title });
          return {
            title: source.title || bylawNum || "Unknown",
            type: "bylaw",
            date: source.date || "",
            link: source.link || source.url || "#",
            case_number: undefined,
            bylaw_number: bylawNum || undefined
          };
        }) || []
      };
      console.log("Bylaw Message sources:", aiMsg.sources);
      setIsTyping(false);
      setMessages(prev => [...prev, aiMsg]);
    },
    onError: (error: any) => {
      console.error("Bylaws search error:", error);
      
      // Check if it's a "conversation too long" or validation error
      const errorDetail = error?.response?.data?.detail || error?.message || "";
      const errorLower = errorDetail.toLowerCase();
      const isConversationTooLong = errorLower.includes("conversation has become too long") || 
                                    errorLower.includes("too long for requested model");
      const isValidationError = errorLower.includes("validation error") || 
                                errorLower.includes("encountered a validation error") ||
                                errorLower.includes("content must be present");
      
      let errorContent: string;
      if ((isConversationTooLong || isValidationError) && currentConversationId) {
        if (isConversationTooLong) {
          errorContent = "⚠️ **Conversation Too Long**\n\nThe conversation history has exceeded the model's context limit. Please start a new conversation by clicking 'New Conversation' in the sidebar to continue.";
        } else {
          errorContent = "⚠️ **Conversation Error**\n\nThe conversation has encountered a validation error. Please start a new conversation by clicking 'New Conversation' in the sidebar to continue.";
        }
        // Clear the conversation ID so the next query will start fresh
        setCurrentConversationId(null);
        // Refresh the sidebar to update the conversation list
        queryClient.invalidateQueries({
          queryKey: ["records-conversations"]
        });
      } else {
        errorContent = "I apologize, but I encountered an error while searching. Please try again.";
      }
      
      const errorMsg: Message = {
        id: Date.now() + 1,
        role: "ai",
        content: errorContent,
        sources: []
      };
      setIsTyping(false);
      setMessages(prev => [...prev, errorMsg]);
    }
  });

  const handleNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setQuery("");
  };

  const handleSelectConversation = (conversationId: number | null) => {
    setCurrentConversationId(conversationId);
    if (conversationId) {
      // Load the conversation directly with the ID to avoid state timing issues
      loadConversationById(conversationId);
    } else {
      setMessages([]);
    }
  };

  const handleTabChange = (tab: "olt" | "bylaws") => {
    setActiveTab(tab);
    // Start new conversation when switching tabs
    handleNewConversation();
  };

  const handleSend = () => {
    if (!query.trim()) return;

    const userMsg: Message = { id: Date.now(), role: "user", content: query };
    setMessages(prev => [...prev, userMsg]);
    setQuery("");
    setIsTyping(true);

    if (activeTab === "olt") {
      oltSearchMutation.mutate(query);
    } else {
      // Use the bylaws agent
      bylawsSearchMutation.mutate(query);
    }
  };

  // Check if user has any municipalities monitoring
  const hasMunicipalities = monitoredMunicipalities && monitoredMunicipalities.length > 0;

  return (
    <div className="w-full h-screen overflow-hidden flex flex-col">
      <div className="flex-1 flex min-h-0">
        {/* Chat History Sidebar */}
        <ChatHistorySidebar
          activeTab={activeTab}
          currentConversationId={currentConversationId}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
        />
        
        <main className="flex-1 flex flex-col min-h-0 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-primary/5 blur-[120px] rounded-full" />
            <div className="absolute top-[20%] -left-[10%] w-[30%] h-[30%] bg-blue-500/5 blur-[100px] rounded-full" />
          </div>

          {/* Header */}
          <header className="h-16 border-b border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm flex items-center justify-between px-8 z-10 relative shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <Sparkles className="h-4 w-4" />
              </div>
              <h1 className="font-semibold text-lg tracking-tight">Municipal Records Intelligence</h1>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-white dark:bg-neutral-900 font-normal text-neutral-500 gap-1.5 pl-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                AI Online
              </Badge>
            </div>
          </header>

          {!hasMunicipalities ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center min-h-[400px] text-center p-8 border-2 border-dashed border-neutral-200 dark:border-neutral-800 rounded-xl bg-neutral-50/50 dark:bg-neutral-900/50"
              >
                <div className="h-16 w-16 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center mb-6">
                  <BookOpen className="h-8 w-8 text-neutral-400" />
                </div>
                <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
                  No Municipal Records Found
                </h3>
                <p className="text-neutral-500 dark:text-neutral-400 max-w-md mb-8 leading-relaxed">
                  You aren't monitoring any municipalities yet. Configure your monitoring criteria to start receiving updates on regulations and bills.
                </p>
                <div className="flex items-center gap-4">
                  <Button variant="outline" className="gap-2 bg-white dark:bg-neutral-900">
                    Learn More
                  </Button>
                  <Button className="gap-2 shadow-md bg-primary hover:bg-primary/90 text-white" onClick={() => navigate('/sources')}>
                    <PlusCircle className="h-4 w-4" />
                    Configure Monitoring
                  </Button>
                </div>
              </motion.div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-h-0">

        {/* Chat Area - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 scroll-smooth min-h-0 relative z-0">
          <div className="max-w-3xl mx-auto space-y-8">
            
            {messages.length === 0 ? (
              /* Empty State */
              (<motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-8"
              >
                <div className="relative">
                  <div className="h-20 w-20 bg-white dark:bg-neutral-900 rounded-2xl shadow-lg border border-neutral-100 dark:border-neutral-800 flex items-center justify-center z-10 relative">
                    <Sparkles className="h-10 w-10 text-primary" />
                  </div>
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                </div>
                <div className="space-y-3 max-w-lg">
                  <h2 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-white">
                    Municipal Intelligence Assistant
                  </h2>
                  <p className="text-neutral-500 text-lg leading-relaxed">
                    Search across thousands of OLT decisions and municipal bylaws. Get instant answers with cited precedents.
                  </p>
                  </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl">
                  <SuggestionCard 
                    icon={<Scale className="h-5 w-5 text-orange-500" />}
                    title="Search OLT Precedents"
                    query="Find recent OLT decisions regarding shadow impacts in downtown Toronto."
                    onClick={() => {
                      handleTabChange("olt");
                      setQuery("Find recent OLT decisions regarding shadow impacts in downtown Toronto.");
                    }}
                  />
                  <SuggestionCard 
                    icon={<ScrollText className="h-5 w-5 text-blue-500" />}
                    title="Query Municipal Bylaws"
                    query="What are the zoning requirements for garden suites in Mississauga?"
                    onClick={() => {
                      handleTabChange("bylaws");
                      setQuery("What are the zoning requirements for garden suites in Mississauga?");
                    }}
                  />
                  </div>
              </motion.div>)
            ) : (
              /* Message History */
              (<div className="space-y-8 pb-8">
                {messages.map((msg) => (
                  <motion.div 
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      "flex gap-4",
                      msg.role === "user" ? "flex-row-reverse" : "flex-row"
                    )}
                  >
                    <div className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center shrink-0",
                      msg.role === "ai" ? "bg-primary text-white shadow-md" : "bg-neutral-200 dark:bg-neutral-800 text-neutral-600"
                    )}>
                      {msg.role === "ai" ? <Sparkles className="h-4 w-4" /> : <User className="h-4 w-4" />}
                    </div>
                    
                    <div className={cn(
                      "max-w-[85%] space-y-4",
                      msg.role === "user" ? "text-right" : "text-left"
                    )}>
                      <div className={cn(
                        "p-4 rounded-2xl inline-block text-sm leading-relaxed shadow-sm",
                        msg.role === "user" 
                          ? "bg-[#1A1C1E] text-white rounded-tr-sm" 
                          : "bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-tl-sm text-neutral-800 dark:text-neutral-200"
                      )}>
                        {msg.role === "ai" ? (
                          <div className="text-base leading-relaxed">
                            <MarkdownWithCitations content={msg.content} sources={msg.sources} />
                          </div>
                        ) : (
                          msg.content
                        )}
                      </div>
                          </div>
                  </motion.div>
                ))}
                {isTyping && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-4"
                  >
                    <div className="h-8 w-8 rounded-full bg-primary text-white shadow-md flex items-center justify-center shrink-0">
                      <Sparkles className="h-4 w-4" />
                    </div>
                    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 rounded-2xl rounded-tl-sm shadow-sm">
                      <div className="flex gap-1 h-4 items-center">
                        <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>)
        )}

          </div>
        </div>

      {/* Input Area - Fixed at bottom */}
        <div className="relative z-10 shrink-0 bg-gradient-to-t from-[#F4F5F7] via-[#F4F5F7] to-transparent dark:from-neutral-950 dark:via-neutral-950 border-t border-neutral-200 dark:border-neutral-800">
          <div className="max-w-3xl mx-auto py-6 px-6 space-y-6">
            
            {/* Mode Switcher (Floating) */}
            <div className="flex justify-center pt-4">
              <div className="bg-white dark:bg-neutral-900 p-1 rounded-full shadow-sm border border-neutral-200 dark:border-neutral-800 inline-flex">
          <button
                  onClick={() => handleTabChange("olt")}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
                    activeTab === "olt" 
                      ? "bg-[#1A1C1E] text-white shadow-md" 
                      : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  )}
                >
                  <Scale className="h-4 w-4" />
            OLT Decisions
          </button>
          <button
                  onClick={() => handleTabChange("bylaws")}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
                    activeTab === "bylaws" 
                      ? "bg-[#1A1C1E] text-white shadow-md" 
                      : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  )}
                >
                  <ScrollText className="h-4 w-4" />
                  Municipal Bylaws
          </button>
        </div>
            </div>

            {/* Input Box */}
            <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-lg border border-neutral-200/60 dark:border-neutral-800 overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <Textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={activeTab === "olt" ? "Ask about tribunal decisions, case precedents, or rulings..." : "Search bylaws across Toronto, Mississauga, and more..."}
                className="w-full min-h-[60px] max-h-[200px] p-4 pr-14 border-none resize-none focus-visible:ring-0 text-base bg-transparent"
              />
              <div className="absolute right-2 bottom-2">
                <Button 
                  size="icon" 
                  className={cn(
                    "h-10 w-10 rounded-xl transition-all", 
                    query.trim() ? "bg-primary text-white hover:bg-primary/90 shadow-md" : "bg-neutral-100 text-neutral-400 hover:bg-neutral-200 dark:bg-neutral-800"
                  )}
            onClick={handleSend}
                  disabled={!query.trim()}
                >
                  <Send className="h-5 w-5" />
                </Button>
        </div>
            </div>
            
            <p className="text-center text-xs text-neutral-400 pb-4">
              Cityscrape AI can make mistakes. Verify important information with official records.
            </p>
          </div>
        </div>
            </div>
          )}
      </main>
      </div>
    </div>
  );
}

function SuggestionCard({ icon, title, query, onClick }: any) {
  return (
    <button 
      onClick={onClick}
      className="flex flex-col gap-3 p-5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl text-left hover:border-primary/50 hover:shadow-md transition-all group h-full"
    >
      <div className="flex items-center justify-between w-full">
        <div className="p-2 bg-neutral-50 dark:bg-neutral-800 rounded-lg group-hover:bg-white dark:group-hover:bg-neutral-700 transition-colors">
          {icon}
        </div>
        <ArrowRight className="h-4 w-4 text-neutral-300 group-hover:text-primary transition-colors -translate-x-2 group-hover:translate-x-0 opacity-0 group-hover:opacity-100" />
      </div>
      <div>
        <h3 className="font-semibold text-neutral-900 dark:text-white mb-1">{title}</h3>
        <p className="text-sm text-neutral-500 line-clamp-2">"{query}"</p>
    </div>
    </button>
  );
}